import React, { useState, useRef, ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  Upload, 
  Link, 
  FileText, 
  Download, 
  AlertCircle, 
  CheckCircle, 
  X,
  Loader2,
  Menu as MenuIcon
} from 'lucide-react';
import api from '../../services/api';

const AIImportMenu: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'file' | 'url'>('file');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { menuId } = useParams<{ menuId: string }>();

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error('Please select a PDF, DOC, or DOCX file');
        return;
      }
      
      if (selectedFile.size > 50 * 1024 * 1024) { // 50MB
        toast.error('File size exceeds 50MB limit');
        return;
      }
      
      setFile(selectedFile);
      setValidationResult(null);
      setValidationError(null);
      setImportResult(null);
    }
  };

  const handleFileUpload = async () => {
    if (!file || !menuId) {
      toast.error('Please select a file and menu');
      return;
    }

    try {
      setIsImporting(true);
      setValidationResult(null);
      setValidationError(null);

      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/menus/${menuId}/import/file`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setImportResult(response.data);
        toast.success('Menu imported successfully!');
        setTimeout(() => {
          navigate(`/menus/${menuId}/edit`);
        }, 2000);
      } else {
        toast.error(response.data.error || 'Import failed');
      }
    } catch (error: any) {
      console.error('Import error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to import menu';
      const suggestion = error.response?.data?.suggestion || '';
      toast.error(`${errorMessage}${suggestion ? ` - ${suggestion}` : ''}`);
      setValidationError(errorMessage);
    } finally {
      setIsImporting(false);
    }
  };

  const handleUrlImport = async () => {
    if (!url || !menuId) {
      toast.error('Please enter a valid URL');
      return;
    }

    try {
      setIsImporting(true);
      setValidationResult(null);
      setValidationError(null);
      setImportResult(null);

      const response = await api.post(`/menus/${menuId}/import/url`, { url });

      if (response.data.success) {
        setImportResult(response.data);
        toast.success('Menu imported successfully from URL!');
        setTimeout(() => {
          navigate(`/menus/${menuId}/edit`);
        }, 2000);
      } else {
        toast.error(response.data.error || 'Import failed');
      }
    } catch (error: any) {
      console.error('URL import error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to import menu from URL';
      const suggestion = error.response?.data?.suggestion || '';
      toast.error(`${errorMessage}${suggestion ? ` - ${suggestion}` : ''}`);
      setValidationError(errorMessage);
    } finally {
      setIsImporting(false);
    }
  };

  const validateUrl = async () => {
    if (!url) {
      setValidationError('Please enter a URL');
      return;
    }

    try {
      const response = await api.post('/menus/import/validate-url', { url });
      if (response.data.success) {
        setValidationResult(response.data.data);
        setValidationError(null);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'URL validation failed';
      setValidationError(errorMessage);
      setValidationResult(null);
    }
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Menu Import</h1>
        <p className="text-gray-600">
          Import your menu from PDF, DOC/DOCX files or URL using AI to automatically extract categories, dishes, prices, and allergens.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`px-4 py-2 font-medium text-sm ${
            activeTab === 'file'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('file')}
        >
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Import from File
          </div>
        </button>
        <button
          className={`px-4 py-2 font-medium text-sm ${
            activeTab === 'url'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('url')}
        >
          <div className="flex items-center gap-2">
            <Link className="w-4 h-4" />
            Import from URL
          </div>
        </button>
      </div>

      {activeTab === 'file' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Import from File
          </h2>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Menu File
            </label>
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                {file ? (
                  <div className="flex flex-col items-center justify-center p-6">
                    <FileText className="w-12 h-12 text-green-500 mb-4" />
                    <p className="font-medium text-gray-900 mb-1">{file.name}</p>
                    <p className="text-sm text-gray-500 mb-4">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile();
                        }}
                        className="px-4 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
                      >
                        Remove
                      </button>
                      <button
                        type="button"
                        onClick={handleFileUpload}
                        disabled={isImporting}
                        className="px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
                      >
                        {isImporting ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            Import Menu
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-12 h-12 text-[#7BD5B5]/40 mb-4" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF, DOC, DOCX (MAX. 50MB)
                    </p>
                  </div>
                )}
                <input
                  id="file-upload"
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileChange}
                />
              </label>
            </div>
          </div>

          {validationError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-red-800">Import Error</h3>
                <p className="text-red-700 text-sm">{validationError}</p>
              </div>
            </div>
          )}

          {importResult && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-green-800">Import Successful!</h3>
                <p className="text-green-700 text-sm">
                  Imported {importResult.stats.categories} categories and {importResult.stats.items} items
                </p>
                <p className="text-green-700 text-sm mt-1">Redirecting to menu editor...</p>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'url' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Link className="w-5 h-5" />
            Import from URL
          </h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Menu Page URL
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/menu"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={validateUrl}
                disabled={!url || isImporting}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50"
              >
                Validate
              </button>
            </div>
          </div>

          {validationResult && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-medium text-blue-800 mb-2">URL Validation Result</h3>
              <div className="text-sm text-blue-700">
                <p>Status: {validationResult.status}</p>
                <p>Accessible: {validationResult.accessible ? 'Yes' : 'No'}</p>
              </div>
            </div>
          )}

          {validationError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-red-800">Validation Error</h3>
                <p className="text-red-700 text-sm">{validationError}</p>
              </div>
            </div>
          )}

          <div className="mb-4">
            <button
              type="button"
              onClick={handleUrlImport}
              disabled={!url || isImporting}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
            >
              {isImporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Import Menu from URL
                </>
              )}
            </button>
          </div>

          {importResult && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-green-800">Import Successful!</h3>
                <p className="text-green-700 text-sm">
                  Imported {importResult.stats.categories} categories and {importResult.stats.items} items
                </p>
                <p className="text-green-700 text-sm mt-1">Redirecting to menu editor...</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
          <MenuIcon className="w-5 h-5" />
          How AI Menu Import Works
        </h3>
        <ul className="text-blue-700 text-sm space-y-2">
          <li>• Upload a PDF, DOC, or DOCX file containing your menu, or enter a URL to a webpage with menu content</li>
          <li>• Our AI will automatically detect menu categories, dishes, prices, and allergens</li>
          <li>• The imported menu will be added to your menu in the appropriate categories</li>
          <li>• You can review and edit the imported items after import</li>
          <li>• For best results, use clear, well-structured menu content</li>
        </ul>
      </div>
    </div>
  );
};

export default AIImportMenu;